import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { getAccounts } from '../../api/client';

interface Account {
  id: number;
  name: string;
}

interface AccountSelectorProps {
  onSelect: (accountId: string, accountName: string, source: 'resolve' | 'user') => void;
  selectedAccountId: string | null;
  requestedAccountId?: string | null;
}

const AccountSelector: React.FC<AccountSelectorProps> = ({
  onSelect,
  selectedAccountId,
  requestedAccountId,
}) => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const data: Account[] = await getAccounts();
        setAccounts(data);
      } catch (err) {
        setError('Failed to load accounts');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (accounts.length === 0) return;
    const selectedAccount = accounts.find(account => String(account.id) === selectedAccountId);
    const requestedAccount = accounts.find(account => String(account.id) === requestedAccountId);
    const resolvedAccount = requestedAccount || (!requestedAccountId ? selectedAccount : null) || accounts[0];
    if (resolvedAccount && String(resolvedAccount.id) !== selectedAccountId) {
      onSelect(String(resolvedAccount.id), resolvedAccount.name, 'resolve');
    }
    // onSelect is intentionally excluded because ChatContainer recreates it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, requestedAccountId, selectedAccountId]);

  useEffect(() => {
    if (!isOpen) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [isOpen]);

  const selectedAccount = accounts.find(account => String(account.id) === selectedAccountId);
  const filteredAccounts = useMemo(() => {
    const search = searchValue.trim().toLowerCase();
    if (!search) return accounts;
    return accounts.filter(account => account.name.toLowerCase().includes(search));
  }, [accounts, searchValue]);

  if (loading) return <div className="context-nav-status">Loading accounts...</div>;
  if (error) return <div className="context-nav-status context-nav-error">{error}</div>;

  return (
    <div className="account-switcher" ref={containerRef}>
      <span className="context-nav-label">Account</span>
      <button
        type="button"
        className="account-switcher-trigger"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(open => !open)}
      >
        <span>{selectedAccount?.name || 'Select account'}</span>
        <ChevronDown size={16} />
      </button>
      {isOpen && (
        <div className="account-switcher-menu">
          <label className="context-search">
            <Search size={15} />
            <input
              autoFocus
              aria-label="Search accounts"
              placeholder="Search accounts"
              value={searchValue}
              onChange={event => setSearchValue(event.target.value)}
            />
          </label>
          <div role="listbox" aria-label="Accounts" className="account-switcher-options">
            {filteredAccounts.map(account => {
              const accountId = String(account.id);
              return (
                <button
                  type="button"
                  role="option"
                  aria-selected={accountId === selectedAccountId}
                  className={`context-nav-option${accountId === selectedAccountId ? ' active' : ''}`}
                  key={account.id}
                  onClick={() => {
                    setIsOpen(false);
                    setSearchValue('');
                    if (accountId !== selectedAccountId) onSelect(accountId, account.name, 'user');
                  }}
                >
                  {account.name}
                </button>
              );
            })}
            {filteredAccounts.length === 0 && <div className="context-nav-empty">No accounts found</div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSelector;
